import React, { FC, useCallback } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Paper,
} from '@mui/material'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import Grid from '@mui/material/Grid'
import {
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { useMetricsDetailQuery } from '@/features/internal/metrics'
import {
  MetricsMedication,
  MetricsAddition,
} from '@/features/internal/metrics/types/metrics'

export const MetricsDetailPage: FC = () => {
  const navigate = useNavigate()
  const { id = '' } = useParams<{ id: string }>()

  // TanStack Query でデータ取得
  const {
    data: metric,
    isLoading: loading,
    error: queryError,
  } = useMetricsDetailQuery({ id })
  const error = queryError?.message ?? null

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getFullYear()}年${
      date.getMonth() + 1
    }月${date.getDate()}日 ${date.getHours()}:${date
      .getMinutes()
      .toString()
      .padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${seconds}秒 (${minutes}分${remainingSeconds}秒)`
  }

  const calculateTimePerMedication = useCallback(
    (audioSeconds: number, medicationCount: number) => {
      if (!audioSeconds || !medicationCount) return '-'
      return `${Math.round(audioSeconds / medicationCount)}秒/剤`
    },
    []
  )

  const groupMedicationsByRP = useCallback(
    (medications: MetricsMedication[]) => {
      return medications.reduce((acc, med) => {
        if (!acc[med.rp_number]) {
          acc[med.rp_number] = []
        }
        acc[med.rp_number].push(med)
        return acc
      }, {} as Record<string, MetricsMedication[]>)
    },
    []
  )

  const handleViewOriginalData = () => {
    if (metric?.yakureki) {
      navigate(`/soap/${metric.yakureki}`)
    }
  }

  if (!metric && !error) {
    return null
  }

  return (
    <PortalLayout>
      {loading && <LoadingIndicator />}

      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/portal/metrics/records')}
            sx={{
              color: colorTokens.grey[500],
              borderColor: colorTokens.border.light,
              minHeight: 40,
              py: 1.5,
              px: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(158, 158, 158, 0.12)',
              '&:hover': {
                borderColor: colorTokens.grey[500],
                backgroundColor: 'rgba(158, 158, 158, 0.18)',
              },
            }}
          >
            一覧に戻る
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              color: colorTokens.text.primary,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AssessmentIcon
              sx={{ fontSize: 32, color: colorTokens.grey[700] }}
            />
            指導実績詳細
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {metric && (
          <Grid container spacing={3}>
            {/* 基本情報 */}
            <Grid size={12}>
              <Card
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: colorTokens.text.primary,
                    }}
                  >
                    基本情報
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          作成日時
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(metric.created_at)}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          処方箋番号
                        </Typography>
                        <Typography variant="body1">
                          {metric.prescription_number}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          医療機関名
                        </Typography>
                        <Typography variant="body1">
                          {metric.medical_institution_name || '-'}
                        </Typography>
                      </Box>
                      {metric.department_name && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            診療科名
                          </Typography>
                          <Typography variant="body1">
                            {metric.department_name}
                          </Typography>
                        </Box>
                      )}
                      {metric.doctor_name && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            処方医師名
                          </Typography>
                          <Typography variant="body1">
                            {metric.doctor_name}
                          </Typography>
                        </Box>
                      )}
                      {metric.pharmacy_name && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            薬局名
                          </Typography>
                          <Typography variant="body1">
                            {metric.pharmacy_name}
                          </Typography>
                        </Box>
                      )}
                      {metric.pharmacist_name && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            薬剤師名
                          </Typography>
                          <Typography variant="body1">
                            {metric.pharmacist_name}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          薬剤数
                        </Typography>
                        <Typography variant="body1">
                          {metric.medication_count}剤
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          最終更新
                        </Typography>
                        <Typography variant="body1">
                          {formatDateTime(metric.updated_at)}
                        </Typography>
                      </Box>
                      {metric.prescription_date && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            処方箋交付日
                          </Typography>
                          <Typography variant="body1">
                            {metric.prescription_date}
                          </Typography>
                        </Box>
                      )}
                      {metric.dispensing_date && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            調剤日
                          </Typography>
                          <Typography variant="body1">
                            {metric.dispensing_date}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          元データ
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<LaunchIcon />}
                          onClick={handleViewOriginalData}
                          sx={{
                            mt: 0.5,
                            color: colorTokens.blue[500],
                            borderColor: colorTokens.blue[500],
                            minHeight: 32,
                            py: 0.5,
                            px: 1.5,
                            borderRadius: 2,
                            backgroundColor: 'rgba(33, 150, 243, 0.04)',
                            '&:hover': {
                              borderColor: colorTokens.blue[600],
                              backgroundColor: 'rgba(33, 150, 243, 0.08)',
                            },
                          }}
                        >
                          薬歴画面を表示
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* 音声・会話情報 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: colorTokens.text.primary,
                    }}
                  >
                    音声・会話情報
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      音声録音
                    </Typography>
                    <Chip
                      label={metric.has_audio ? 'あり' : 'なし'}
                      size="small"
                      sx={{
                        mt: 0.5,
                        backgroundColor: metric.has_audio
                          ? colorTokens.special.highlight
                          : colorTokens.grey[50],
                        color: metric.has_audio
                          ? colorTokens.blue[600]
                          : colorTokens.text.secondary,
                        border: `1px solid ${
                          metric.has_audio
                            ? colorTokens.blue[50]
                            : colorTokens.border.light
                        }`,
                      }}
                    />
                  </Box>
                  {metric.has_audio && (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          指導時間
                        </Typography>
                        <Typography variant="body1">
                          {formatDuration(metric.audio_duration_seconds)}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          薬剤あたり指導時間
                        </Typography>
                        <Typography variant="body1">
                          {calculateTimePerMedication(
                            metric.audio_duration_seconds,
                            metric.medication_count
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                          やりとり回数
                        </Typography>
                        <Typography variant="body1">
                          {metric.conversation_exchange_count}回
                        </Typography>
                      </Box>
                      {metric.speech_rate !== undefined && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            話速
                          </Typography>
                          <Typography variant="body1">
                            {metric.speech_rate
                              ? `${metric.speech_rate}文字/分`
                              : '-'}
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* SOAP情報 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: colorTokens.text.primary,
                    }}
                  >
                    SOAP情報
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      SOAP生成
                    </Typography>
                    <Chip
                      label={metric.has_soap ? '済み' : '未生成'}
                      size="small"
                      sx={{
                        mt: 0.5,
                        backgroundColor: metric.has_soap
                          ? colorTokens.special.highlight
                          : colorTokens.grey[50],
                        color: metric.has_soap
                          ? colorTokens.blue[600]
                          : colorTokens.text.secondary,
                        border: `1px solid ${
                          metric.has_soap
                            ? colorTokens.blue[50]
                            : colorTokens.border.light
                        }`,
                      }}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      SOAPコピー
                    </Typography>
                    <Chip
                      label={metric.soap_copied ? '済み' : '未実行'}
                      size="small"
                      sx={{
                        mt: 0.5,
                        backgroundColor: metric.soap_copied
                          ? colorTokens.special.highlight
                          : colorTokens.grey[50],
                        color: metric.soap_copied
                          ? colorTokens.blue[600]
                          : colorTokens.text.secondary,
                        border: `1px solid ${
                          metric.soap_copied
                            ? colorTokens.blue[50]
                            : colorTokens.border.light
                        }`,
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* 加算情報 */}
            <Grid size={12}>
              <Card
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: colorTokens.text.primary,
                    }}
                  >
                    加算情報 ({metric.additions.length}件)
                  </Typography>
                  {metric.additions.length > 0 ? (
                    <Grid container spacing={1}>
                      {metric.additions.map(
                        (addition: MetricsAddition, index: number) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                            <Paper
                              sx={{
                                p: 2,
                                backgroundColor: colorTokens.background.paper,
                                border: `1px solid ${colorTokens.border.light}`,
                              }}
                            >
                              <Typography variant="body1">
                                {addition.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {addition.code}
                              </Typography>
                            </Paper>
                          </Grid>
                        )
                      )}
                    </Grid>
                  ) : (
                    <Typography color="textSecondary">
                      加算情報はありません
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* 処方薬剤情報 */}
            {metric.medications && metric.medications.length > 0 && (
              <Grid size={12}>
                <Card
                  sx={{
                    backgroundColor: colorTokens.background.paper,
                    borderRadius: 2,
                    border: `1px solid ${colorTokens.border.light}`,
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: colorTokens.text.primary,
                      }}
                    >
                      処方薬剤情報
                    </Typography>

                    <>
                      {(() => {
                        const groupedMedications = groupMedicationsByRP(
                          metric.medications
                        )
                        const rpEntries = Object.entries(groupedMedications)
                        return rpEntries.map(([rpNumber, meds], rpIndex) => (
                          <Box
                            key={rpNumber}
                            sx={{
                              pb: 1.5,
                              mb: rpIndex < rpEntries.length - 1 ? 1.5 : 0,
                              borderBottom:
                                rpIndex < rpEntries.length - 1
                                  ? `1px solid ${colorTokens.border.light}`
                                  : 'none',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 1,
                                color: colorTokens.blue[600],
                              }}
                            >
                              RP {rpNumber}
                            </Typography>

                            {meds.map((med, index) => (
                              <Box key={index} sx={{ mb: 0.5 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 2,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: colorTokens.text.primary,
                                    }}
                                  >
                                    {med.name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: colorTokens.text.primary,
                                      ml: 'auto',
                                    }}
                                  >
                                    {med.dosage} {med.unit}
                                  </Typography>
                                </Box>

                                {med.drug_comment && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: colorTokens.text.secondary,
                                      ml: 2,
                                      display: 'block',
                                    }}
                                  >
                                    薬品コメント: {med.drug_comment}
                                  </Typography>
                                )}
                              </Box>
                            ))}

                            {/* 用法（RPごとに共通） */}
                            {meds[0].usage && (
                              <Box sx={{ mt: 1, ml: 2 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: colorTokens.text.primary,
                                  }}
                                >
                                  用法: {meds[0].usage}
                                </Typography>

                                {meds[0].usage_comment && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: colorTokens.text.secondary,
                                      display: 'block',
                                    }}
                                  >
                                    用法コメント: {meds[0].usage_comment}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        ))
                      })()}
                    </>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </PortalLayout>
  )
}
